import Dexie from 'dexie';

// Initialize IndexedDB database
export const db = new Dexie('InventoryDB');

// Define database schema
db.version(1).stores({
  products: '++id, sku, name, shopifyId, createdAt',
  inventory: '++id, productId, location, quantity, lastSynced',
  transactions: '++id, productId, type, timestamp, syncStatus, location',
  syncQueue: '++id, status, createdAt, retryCount'
});

// Add compound index for querying inventory by productId and location
db.version(2).stores({
  products: '++id, sku, name, shopifyId, createdAt',
  inventory: '++id, productId, location, [productId+location], quantity, lastSynced',
  transactions: '++id, productId, type, timestamp, syncStatus, location',
  syncQueue: '++id, status, createdAt, retryCount'
});

// Product model
export class Product {
  constructor(data = {}) {
    this.id = data.id;
    this.sku = data.sku || '';
    this.name = data.name || '';
    this.description = data.description || '';
    this.price = data.price || 0;
    this.photoUrl = data.photoUrl || '';
    this.shopifyId = data.shopifyId || null;
    this.createdAt = data.createdAt || new Date().toISOString();
  }
}

// Inventory model
export class Inventory {
  constructor(data = {}) {
    this.id = data.id;
    this.productId = data.productId;
    this.quantity = data.quantity || 0;
    this.location = data.location || 'home'; // 'home', 'market', 'shopify'
    this.lowStockThreshold = data.lowStockThreshold || 10;
    this.lastSynced = data.lastSynced || null;
  }

  isLowStock() {
    return this.quantity < this.lowStockThreshold;
  }

  isCriticalStock() {
    return this.quantity < 5;
  }
}

// Transaction model
export class Transaction {
  constructor(data = {}) {
    this.id = data.id;
    this.productId = data.productId;
    this.type = data.type; // 'sale', 'restock', 'adjustment', 'transfer'
    this.quantity = data.quantity || 0;
    this.timestamp = data.timestamp || new Date().toISOString();
    this.location = data.location || 'home';
    this.syncStatus = data.syncStatus || 'pending'; // 'pending', 'completed', 'failed'
    this.notes = data.notes || '';
  }
}

// Sync Queue model
export class SyncQueueItem {
  constructor(data = {}) {
    this.id = data.id;
    this.action = data.action; // 'create', 'update', 'delete'
    this.resourceType = data.resourceType; // 'product', 'inventory', 'transaction'
    this.payload = data.payload || {};
    this.status = data.status || 'pending'; // 'pending', 'completed', 'failed'
    this.createdAt = data.createdAt || new Date().toISOString();
    this.retryCount = data.retryCount || 0;
    this.lastError = data.lastError || null;
  }
}

// Database helper functions
export const dbHelpers = {
  // Products
  async getAllProducts() {
    return await db.products.toArray();
  },

  async getProductById(id) {
    return await db.products.get(id);
  },

  async getProductBySKU(sku) {
    return await db.products.where('sku').equals(sku).first();
  },

  async createProduct(product) {
    const newProduct = new Product(product);
    const id = await db.products.add(newProduct);
    return { ...newProduct, id };
  },

  async updateProduct(id, updates) {
    await db.products.update(id, updates);
    return await db.products.get(id);
  },

  async deleteProduct(id) {
    await db.products.delete(id);
  },

  // Inventory
  async getInventoryByProductId(productId) {
    return await db.inventory.where('productId').equals(productId).toArray();
  },

  async getInventoryByLocation(location) {
    return await db.inventory.where('location').equals(location).toArray();
  },

  async updateInventory(productId, location, quantity) {
    // Find existing inventory by filtering instead of compound index
    const allInventory = await db.inventory
      .where('productId')
      .equals(productId)
      .toArray();
    
    const existing = allInventory.find(inv => inv.location === location);

    if (existing) {
      await db.inventory.update(existing.id, { 
        quantity,
        lastSynced: new Date().toISOString()
      });
      return await db.inventory.get(existing.id);
    } else {
      const newInventory = new Inventory({ productId, location, quantity });
      const id = await db.inventory.add(newInventory);
      return { ...newInventory, id };
    }
  },

  async getLowStockItems() {
    const inventory = await db.inventory.toArray();
    return inventory.filter(item => item.quantity < item.lowStockThreshold);
  },

  // Transactions
  async createTransaction(transaction) {
    const newTransaction = new Transaction(transaction);
    const id = await db.transactions.add(newTransaction);
    
    // Update inventory based on transaction - filter manually instead of compound index
    const allInventory = await db.inventory
      .where('productId')
      .equals(transaction.productId)
      .toArray();
    
    const inventory = allInventory.find(inv => inv.location === transaction.location);

    if (inventory) {
      let newQuantity = inventory.quantity;
      if (transaction.type === 'sale') {
        newQuantity -= transaction.quantity;
      } else if (transaction.type === 'restock') {
        newQuantity += transaction.quantity;
      } else if (transaction.type === 'adjustment') {
        newQuantity = transaction.quantity;
      }
      await db.inventory.update(inventory.id, { quantity: Math.max(0, newQuantity) });
    }

    return { ...newTransaction, id };
  },

  async getTransactionsByProduct(productId) {
    return await db.transactions
      .where('productId')
      .equals(productId)
      .reverse()
      .sortBy('timestamp');
  },

  async getPendingTransactions() {
    return await db.transactions
      .where('syncStatus')
      .equals('pending')
      .toArray();
  },

  // Sync Queue
  async addToSyncQueue(action, resourceType, payload) {
    const item = new SyncQueueItem({ action, resourceType, payload });
    const id = await db.syncQueue.add(item);
    return { ...item, id };
  },

  async getPendingSyncItems() {
    return await db.syncQueue
      .where('status')
      .equals('pending')
      .sortBy('createdAt');
  },

  async updateSyncItem(id, updates) {
    await db.syncQueue.update(id, updates);
  },

  async clearCompletedSyncItems() {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    await db.syncQueue
      .where('status')
      .equals('completed')
      .and(item => new Date(item.createdAt) < weekAgo)
      .delete();
  },

  // Utility
  async clearAllData() {
    await db.products.clear();
    await db.inventory.clear();
    await db.transactions.clear();
    await db.syncQueue.clear();
  },

  async exportData() {
    return {
      products: await db.products.toArray(),
      inventory: await db.inventory.toArray(),
      transactions: await db.transactions.toArray(),
      syncQueue: await db.syncQueue.toArray(),
    };
  }
};

export default db;
